import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Lock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT, tr } from "@/lib/i18n";

const formatErr = (detail) => {
  if (detail == null) return tr("Something went wrong. Please try again.", "Une erreur s'est produite. Veuillez réessayer.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  return String(detail);
};

export const ResetPassword = () => {
  useT();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr(tr("Passwords do not match.", "Les mots de passe ne correspondent pas."));
      return;
    }
    if (password.length < 8 || !/\d/.test(password)) {
      setErr(tr("Password must be at least 8 characters and include a number.", "Le mot de passe doit comporter au moins 8\u00a0caractères, dont un chiffre."));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: password });
      setUser(r.data);
      toast.success(tr("Password updated", "Mot de passe mis à jour"));
      navigate("/projects");
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="max-w-md">
          <div className="overline mb-3">{tr("Access", "Accès")}</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">{tr("Set a new password", "Choisissez un nouveau mot de passe")}</h1>
          <p className="mt-4 text-base" style={{ color: "var(--muted)" }}>
            {tr("Pick a password with at least 8 characters and one number.", "Choisissez un mot de passe d'au moins 8\u00a0caractères, dont un chiffre.")}
          </p>

          {tokenMissing ? (
            <div className="mt-8 p-4" style={{ background: "rgba(184,50,50,0.08)", color: "#B83232", border: "1px solid rgba(184,50,50,0.2)" }} data-testid="reset-token-missing">
              {tr("This reset link is missing its token. Please request a new reset email from the sign-in page.", "Ce lien de réinitialisation est incomplet. Demandez un nouvel e-mail de réinitialisation depuis la page de connexion.")}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-3" data-testid="reset-password-form">
              <label className="block">
                <div className="text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--muted)" }}>{tr("New password", "Nouveau mot de passe")}</div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                  <input
                    type="password" required minLength={8} autoComplete="new-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm"
                    style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                    data-testid="input-new-password"
                  />
                </div>
              </label>
              <label className="block">
                <div className="text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--muted)" }}>{tr("Confirm new password", "Confirmez le nouveau mot de passe")}</div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                  <input
                    type="password" required minLength={8} autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm"
                    style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                    data-testid="input-confirm-password"
                  />
                </div>
              </label>

              {err && (
                <div className="text-sm p-3" style={{ background: "rgba(184,50,50,0.08)", color: "#B83232", border: "1px solid rgba(184,50,50,0.2)" }} data-testid="reset-error">
                  {err}
                </div>
              )}

              <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="submit-reset">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                {busy ? tr("Updating…", "Mise à jour…") : tr("Update password & sign in", "Mettre à jour et se connecter")}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
